/**
 * Income-qualification lane resolution.
 *
 * The lane is a total pure function of two values the funnel already captures, `state.gender` and
 * `state.income`, so the client and the server derive it independently and cannot disagree. It is
 * never transported: no new field in the PATCH, no new round trip.
 *
 * This module has ZERO imports, and that is a hard requirement rather than a style preference:
 * `functions.js` imports `api.js`, which reads `import.meta.env`, so neither can be loaded by plain
 * Node. A module with no imports can be unit-tested with `node --test` and no new dependency.
 *
 * Mirrored byte for byte in `server/src/incomeLane.js`. ACTIVE_RULESET must hold the same value in
 * both files at every deploy.
 *
 * @author Cristian David
 * @since 2026-09-11
 */

export const LANE_BELOW_FLOOR = 'below_floor';
export const LANE_BORDERLINE = 'borderline';
export const LANE_QUALIFIED = 'qualified';
export const LANE_UNCLASSIFIED = 'unclassified';

export const RULESET_THREE_LANE = 'three-lane-2026-08';
export const RULESET_LEGACY = 'legacy-single-threshold';

/**
 * KILL SWITCH. Mirrored in `server/src/incomeLane.js`.
 *
 * Deploy 1 ships every screen and every copy change with this set to RULESET_LEGACY, so routing is
 * byte-identical to today and the rewrite can be verified on its own. Deploy 2 flips it. A one-line
 * change plus a rebuild reverts the whole feature without touching next(), back() or any component.
 *
 * NOTE: this switch controls ROUTING ONLY. The new price and the new copy go live with deploy 1.
 */
export const ACTIVE_RULESET = RULESET_THREE_LANE;

/**
 * Turns the UI string the visitor selected into a domain token.
 *
 * The only place in the system where that conversion happens for lane purposes, so if the step-0
 * copy ever changes this is the single function to update. `1_LookingFor.jsx:43` performs a similar
 * conversion for cookies and analytics; the two are deliberately NOT shared, because that one maps
 * anything that is not 'I am a man' to 'female' — acceptable for a cookie, wrong for a lane.
 *
 * @param {string} gender - the value held in `state.gender`
 * @returns {'male'|'female'|'unknown'}
 */
export function normalizeGender(gender) {
  if (gender === 'I am a man') return 'male';
  if (gender === 'I am a woman') return 'female';
  return 'unknown';
}

const TABLES = {
  [RULESET_THREE_LANE]: {
    male: {
      '0-45': LANE_BELOW_FLOOR,
      '45-60': LANE_BELOW_FLOOR,
      '60-75': LANE_BORDERLINE,
      '75-100': LANE_QUALIFIED,
      '100-150': LANE_QUALIFIED,
      '150-250': LANE_QUALIFIED,
      '250-500': LANE_QUALIFIED,
      '500+': LANE_QUALIFIED,
    },
    female: {
      '0-45': LANE_BELOW_FLOOR,
      '45-60': LANE_BELOW_FLOOR,
      '60-75': LANE_BELOW_FLOOR,
      '75-100': LANE_BORDERLINE,
      '100-150': LANE_QUALIFIED,
      '150-250': LANE_QUALIFIED,
      '250-500': LANE_QUALIFIED,
      '500+': LANE_QUALIFIED,
    },
  },
  // Reproduces TODAY'S behaviour exactly (guards at stepsContext.jsx:210-222):
  // lowIncome -> screen 9 ; everything else -> step 12. Never returns below_floor.
  [RULESET_LEGACY]: {
    male: {
      '0-45': LANE_BORDERLINE,
      '45-60': LANE_BORDERLINE,
      '60-75': LANE_BORDERLINE,
      '75-100': LANE_QUALIFIED,
      '100-150': LANE_QUALIFIED,
      '150-250': LANE_QUALIFIED,
      '250-500': LANE_QUALIFIED,
      '500+': LANE_QUALIFIED,
    },
    female: {
      '0-45': LANE_BORDERLINE,
      '45-60': LANE_BORDERLINE,
      '60-75': LANE_BORDERLINE,
      '75-100': LANE_QUALIFIED,
      '100-150': LANE_QUALIFIED,
      '150-250': LANE_QUALIFIED,
      '250-500': LANE_QUALIFIED,
      '500+': LANE_QUALIFIED,
    },
  },
};

/**
 * Resolves the lane a visitor belongs to.
 *
 * Total: every input returns a lane, and anything the tables cannot classify returns
 * LANE_UNCLASSIFIED rather than throwing. In the real funnel that case is unreachable, because
 * `8_Income.jsx:38` blocks advancing without an income and `1_LookingFor.jsx:40-41` without a
 * gender. It is a safety net, not a use case.
 *
 * @param {string} gender - `state.gender`, the UI string
 * @param {string} income - `state.income`, one of the 8 bracket keys
 * @param {string} [ruleset=ACTIVE_RULESET] - which threshold table to apply
 * @returns {'below_floor'|'borderline'|'qualified'|'unclassified'}
 */
export function resolveIncomeLane(gender, income, ruleset = ACTIVE_RULESET) {
  const table = TABLES[ruleset];
  if (!table) return LANE_UNCLASSIFIED;

  const byGender = table[normalizeGender(gender)];
  if (!byGender) return LANE_UNCLASSIFIED;

  // Own-property check, not a truthiness check: byGender['__proto__'] would otherwise
  // return Object.prototype, which is truthy and is not a lane.
  if (!Object.prototype.hasOwnProperty.call(byGender, income)) return LANE_UNCLASSIFIED;

  return byGender[income];
}

/**
 * Whether a lane should navigate as if it were qualified.
 *
 * `unclassified` routes as `qualified` on purpose, and the asymmetry of harm is the reason: someone
 * who should have gone to the free network reaching booking is exactly today's behaviour, so it is
 * a zero regression. Someone qualified being sent to the free network is irreversible revenue loss.
 *
 * Navigation only. `unclassified` is never persisted and never sent to the CRM.
 *
 * @param {string} lane - a value returned by resolveIncomeLane
 * @returns {boolean}
 */
export function routesAsQualified(lane) {
  return lane === LANE_QUALIFIED || lane === LANE_UNCLASSIFIED;
}
